import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import CallIcon from "@mui/icons-material/Call";
import imageCompression from 'browser-image-compression';
import {
  Typography,
  Button,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Backdrop,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Stack,
  Fade,
  useTheme,
  useMediaQuery,
  Paper
} from "@mui/material";
import Dialog from "@mui/material/Dialog";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import IconButton from "@mui/material/IconButton";
import {
  getOrderDetails,
  updateOrderStatus,
  completeOrder,
  getUpiDetails,
  uploadPaymentProof
} from "../api/deliveryApi";
import { QRCodeCanvas } from "qrcode.react";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import ReportOrderDialog from "./ReportOrderDialog";
import { cancelOrderByDelivery } from "../api/deliveryApi";

export default function OrderDetails() {
  const { id } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [data, setData] = useState({
    items: [],
    user: {},
    address: {}
  });

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openCancel, setOpenCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancelInput, setShowCancelInput] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);

  // 💳 PAYMENT STATE
  const [openPayment, setOpenPayment] = useState(false);
  const [method, setMethod] = useState("");
  const [note, setNote] = useState("");
  const [upiData, setUpiData] = useState(null);
  const [paymentImage, setPaymentImage] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // 🔄 LOADING STATES
  const [actionLoading, setActionLoading] = useState(false); // Used for Payments/Cancels
  const [statusLoading, setStatusLoading] = useState(false); // Used for Status updates
  const [loadingText, setLoadingText] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);
  const navigate = useNavigate();

  const fetchData = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
        setActionLoading(true);
        setLoadingText("Loading order details...");
      }

      const res = await getOrderDetails(id);

      if (res.data?.error) {
        setError(res.data.error);
        return;
      }

      setData({
        items: res.data.items || [],
        user: res.data.user || {},
        address: res.data.address || {},
        ...res.data
      });
    } catch (err) {
      setError("Failed to load order");
    } finally {
      if (showLoader) {
        setLoading(false);
        setActionLoading(false);
      }
    }
  }, [id]);

  useEffect(() => {
    fetchData(true);
  }, [fetchData]);

  const updateStatus = async (status) => {
    try {
      setStatusLoading(true);
      await updateOrderStatus({
        order_id: id,
        status
      });
      await fetchData(false); // Silent refresh, no screen flash
    } catch (err) {
      alert(err.response?.data?.error || "Update failed");
    } finally {
      setStatusLoading(false);
    }
  };

  // 🚨 OPEN REPORT
  const handleOpenCancel = () => {
    setCancelReason("");
    setShowCancelInput(false);
    setOpenCancel(true);
  };

  // 🚨 CANCEL ORDER
  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      alert("Please enter cancel reason");
      return;
    }

    try {
      setActionLoading(true);
      setLoadingText("Cancelling order...");

      await cancelOrderByDelivery({
        order_id: id,
        reason: cancelReason
      });

      setOpenCancel(false);
      setConfirmCancelOpen(false);
      setCancelReason("");

      // ✅ REDIRECT AFTER CANCEL
      if (data?.batch_id) {
        navigate(`/batch/${data.batch_id}`);
      } else {
        navigate("/dashboard");
      }
    } catch (err) {
      alert(err.response?.data?.error || "Cancel failed");
    } finally {
      setActionLoading(false);
    }
  };

  const openMap = (lat, lng) => {
    if (!lat || !lng) return;
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank", "noopener,noreferrer");
  };

  // 🔥 OPEN PAYMENT + FETCH UPI
  const handleOpenPayment = async () => {
    try {
      setActionLoading(true);
      setLoadingText("Loading payment Page...");
      const res = await getUpiDetails(id);
      setUpiData(res.data);
      setActionLoading(false);
      setOpenPayment(true);
    } catch (err) {
      setActionLoading(false);
      alert("Failed to load payment QR");
    }
  };

  // 🔥 SUBMIT PAYMENT
  const handleSubmitPayment = async () => {
    try {
      setActionLoading(true);
      setLoadingText("Processing payment...");
      let paymentProofUrl = note || "";

      if (method === "UPI") {
        if (!paymentImage) {
          setActionLoading(false);
          alert("Upload payment screenshot");
          return;
        }
        setLoadingText("Uploading payment proof...");
        setUploading(true);

        const formData = new FormData();
        formData.append("file", paymentImage);

        const uploadRes = await uploadPaymentProof(formData);
        paymentProofUrl = uploadRes.data.url;
        setUploading(false);
      }

      setLoadingText("Completing delivery...");
      const completeRes = await completeOrder({
        order_id: id,
        payment_method: method,
        payment_proof: paymentProofUrl,
        amount: data.final_amount
      });

      if (completeRes.data?.success === false) {
        throw new Error(completeRes.data.error || "Payment could not be recorded");
      }

      await updateOrderStatus({
        order_id: id,
        status: "DELIVERED"
      });

      await fetchData(false);

      setOpenPayment(false);
      setMethod("");
      setNote("");
      setPaymentImage(null);
      setActionLoading(false);
      setSuccessOpen(true);

      setTimeout(() => {
        if (data?.batch_id) {
          navigate(`/batch/${data.batch_id}`);
        } else {
          navigate("/dashboard");
        }
      }, 2500);
    } catch (err) {
      setUploading(false);
      setActionLoading(false);
      alert(err.response?.data?.error || "Payment failed");
    }
  };

  // =========================
  // REUSABLE UI STYLES
  // =========================
  const whiteCardStyle = {
    borderRadius: "20px",
    boxShadow: "0 8px 24px rgba(0,0,0,0.04)",
    border: "1px solid #edf2f7",
    mb: 3
  };

  // Status Check for progressive button
  const orderStatus = data?.status;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)",
        pt: { xs: 10, sm: 12 }, 
        pb: { xs: 10, sm: 12 }, 
        px: { xs: 2, sm: 3, md: 4 },
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: "700px", pb: 10 }}>
        {error && (
          <Typography color="error" sx={{ textAlign: "center", my: 2, fontWeight: "bold" }}>
            {error}
          </Typography>
        )}

        <Fade in={!loading && !error && !actionLoading}>
          <Box>
            {/* ========================= */}
            {/* 🔥 HEADER SECTION (RESPONSIVE WHITE) */}
            {/* ========================= */}
            <Paper
              elevation={0}
              sx={{
                p: { xs: 2.5, sm: 4 }, // 👈 Scaled padding for mobile
                borderRadius: "24px",
                background: "#ffffff",
                color: "#1e293b",
                boxShadow: "0 12px 32px rgba(0, 0, 0, 0.05)",
                border: "1px solid #f1f5f9",
                mb: { xs: 3, sm: 4 },
                position: "relative",
                overflow: "hidden"
              }}
            >
              <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, background: 'radial-gradient(circle, rgba(255,107,107,0.04) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />
              <Box sx={{ position: 'absolute', bottom: -50, left: -20, width: 150, height: 150, background: 'radial-gradient(circle, rgba(255,142,83,0.04) 0%, rgba(255,255,255,0) 70%)', borderRadius: '50%' }} />

              <Box sx={{ position: 'relative', zIndex: 1 }}>
                
                {/* TOP ROW: Icon, Title, and Chip */}
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2, flexWrap: "wrap", gap: 1.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1.5, sm: 2 } }}>
                    <IconButton
                      onClick={() => navigate(-1)}
                      sx={{
                        width: { xs: 38, sm: 44 }, 
                        height: { xs: 38, sm: 44 },
                        background: "#f8fafc", color: "#475569",
                        border: "1px solid #e2e8f0", "&:hover": { background: "#e2e8f0" },
                        flexShrink: 0
                      }}
                    >
                      <ArrowBackIosNewIcon sx={{ fontSize: { xs: 16, sm: 18 }, ml: "2px" }} />
                    </IconButton>
                    <Typography 
                      sx={{ 
                        fontWeight: 900, 
                        letterSpacing: "-0.5px", 
                        color: "#0f172a", 
                        fontSize: "clamp(1.4rem, 6vw, 2.2rem)", // 👈 Fluid size scales perfectly for 6-digit IDs
                        lineHeight: 1.1, 
                        wordBreak: "break-word" 
                      }}
                    >
                      Order #{id}
                    </Typography>
                  </Box>
                  <Chip
                    label={orderStatus?.replace("_", " ") || "PENDING"}
                    sx={{
                      background: "#f1f5f9", color: "#334155", fontWeight: "800", 
                      fontSize: { xs: "0.7rem", sm: "0.75rem" }, 
                      letterSpacing: "0.5px", border: "1px solid #e2e8f0",
                      mt: { xs: 0.5, sm: 1 }
                    }}
                  />
                </Box>

                {/* MIDDLE ROW: Description and Price */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', mb: { xs: 2.5, sm: 3 }, gap: 2 }}>
                  <Typography sx={{ color: "#64748b", maxWidth: "60%", fontWeight: 500, fontSize: { xs: "0.85rem", sm: "0.95rem" }, lineHeight: 1.4 }}>
                    Manage your order delivery and update status on the go.
                  </Typography>
                  <Typography sx={{ color: "#10b981", fontWeight: 900, fontSize: { xs: "2.2rem", sm: "2.5rem" }, lineHeight: 1 }}>
                    ₹{data.final_amount || 0}
                  </Typography>
                </Box>

                {/* 🔥 STATUS BUTTONS - PROGRESSIVE FLOW */}
                {orderStatus !== "DELIVERED" ? (
                  <Box sx={{ mt: 1 }}>
                    {orderStatus === "ASSIGNED" && (
                      <Button
                        variant="contained"
                        onClick={() => updateStatus("AT_VENDOR")}
                        disabled={statusLoading}
                        fullWidth
                        sx={{
                          borderRadius: "14px", background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)", color: "white",
                          "&:hover": { background: "linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)" }, py: 1.6, 
                          boxShadow: "0 8px 20px rgba(255, 107, 107, 0.25)", fontWeight: "800", fontSize: "1.05rem", textTransform: "none",
                          "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" }
                        }}
                      >
                        {statusLoading ? <CircularProgress size={24} color="inherit" /> : "🏪 Reached Vendor"}
                      </Button>
                    )}

                    {orderStatus === "AT_VENDOR" && (
                      <Button
                        variant="contained"
                        onClick={() => updateStatus("PICKED_UP")}
                        disabled={statusLoading}
                        fullWidth
                        sx={{
                          borderRadius: "14px", background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)", color: "white",
                          "&:hover": { background: "linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)" }, py: 1.6, 
                          boxShadow: "0 8px 20px rgba(255, 107, 107, 0.25)", fontWeight: "800", fontSize: "1.05rem", textTransform: "none",
                          "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" }
                        }}
                      >
                        {statusLoading ? <CircularProgress size={24} color="inherit" /> : "🛍️ Picked Up"}
                      </Button>
                    )}

                    {orderStatus === "PICKED_UP" && (
                      <Button
                        variant="contained"
                        onClick={() => updateStatus("OUT_FOR_DELIVERY")}
                        disabled={statusLoading}
                        fullWidth
                        sx={{
                          borderRadius: "14px", background: "linear-gradient(45deg, #FF6B6B 30%, #FF8E53 90%)", color: "white",
                          "&:hover": { background: "linear-gradient(45deg, #FF8E53 30%, #FF6B6B 90%)" }, py: 1.6, 
                          boxShadow: "0 8px 20px rgba(255, 107, 107, 0.25)", fontWeight: "800", fontSize: "1.05rem", textTransform: "none",
                          "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" }
                        }}
                      >
                        {statusLoading ? <CircularProgress size={24} color="inherit" /> : "🛵 Start Delivery"}
                      </Button>
                    )}

                    {orderStatus === "OUT_FOR_DELIVERY" && (
                      <Button
                        variant="contained"
                        onClick={handleOpenPayment}
                        disabled={actionLoading}
                        fullWidth
                        sx={{
                          borderRadius: "14px", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", color: "white",
                          "&:hover": { background: "linear-gradient(135deg, #059669 0%, #047857 100%)" }, py: 1.6, 
                          fontWeight: "800", fontSize: "1.05rem", textTransform: "none", boxShadow: "0 8px 20px rgba(16, 185, 129, 0.25)",
                          "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" }
                        }}
                      >
                        {actionLoading ? <CircularProgress size={24} color="inherit" /> : "✅ Mark as Delivered"}
                      </Button>
                    )}
                  </Box>
                ) : (
                  <Chip
                    label="✅ Order Delivered Successfully"
                    sx={{
                      py: 3, width: "100%", fontSize: "1rem", fontWeight: "800",
                      background: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0"
                    }}
                  />
                )}
              </Box>
            </Paper>
            {/* ========================= */}
            {/* CUSTOMER & ADDRESS CARD */}
            {/* ========================= */}
            <Card sx={whiteCardStyle}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h6" fontWeight="800" sx={{ color: "#1a202c", mb: 2, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                  👤 Customer Details
                </Typography>

                <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 1 }}>
                  
                  {/* 👈 MODIFIED: Added wrapper with space-between for the call button */}
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
                      <Box sx={{ fontSize: "1.2rem", mt: 0.2 }}>👤</Box>
                      <Box>
                        <Typography fontWeight="700" sx={{ color: "#2d3748" }}>{data.user?.name || "N/A"}</Typography>
                        <Typography variant="body2" sx={{ color: "#718096" }}>{data.user?.phone || "N/A"}</Typography>
                      </Box>
                    </Box>

                    {/* 👈 NEW: Quick Call Button */}
                    {data.user?.phone && (
                      <IconButton
                        component="a"
                        href={`tel:${data.user.phone}`}
                        sx={{
                          background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", // Emerald green for call
                          color: "white",
                          boxShadow: "0 4px 14px rgba(16, 185, 129, 0.3)",
                          width: 42,
                          height: 42,
                          "&:hover": { background: "linear-gradient(135deg, #059669 0%, #047857 100%)" }
                        }}
                      >
                        <CallIcon sx={{ fontSize: 20 }} />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", background: "#f8fafc", p: 1.5, borderRadius: "12px" }}>
                    <Box sx={{ fontSize: "1.2rem", mt: 0.2 }}>📍</Box>
                    <Typography variant="body2" sx={{ color: "#4a5568", lineHeight: 1.5 }}>
                      {data.address?.flat ? `${data.address.flat}, ` : ""}
                      {data.address?.building ? `${data.address.building}, ` : ""}
                      {data.address?.landmark || ""}
                    </Typography>
                  </Box>
                </Box>

                <Button
                  fullWidth
                  variant="outlined"
                  sx={{
                    mt: 2,
                    borderRadius: "12px",
                    py: 1.5,
                    fontWeight: "bold",
                    borderColor: "#cbd5e1",
                    color: "#475569",
                    "&:hover": { borderColor: "#94a3b8", background: "#f8fafc" }
                  }}
                  onClick={() => openMap(data.lat, data.lng)}
                >
                  🧭 Navigate to Customer
                </Button>
              </CardContent>
            </Card>

            {/* ========================= */}
            {/* ITEMS CARD */}
            {/* ========================= */}
            <Card sx={whiteCardStyle}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
                <Typography variant="h6" fontWeight="800" sx={{ color: "#1a202c", mb: 2, fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
                  📦 Order Items
                </Typography>

                <Stack spacing={1.5}>
                  {data.items?.length > 0 ? (
                    data.items.map((i, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          borderBottom: idx !== data.items.length - 1 ? "1px solid #f1f5f9" : "none",
                          pb: idx !== data.items.length - 1 ? 1.5 : 0
                        }}
                      >
                        <Typography variant="body1" fontWeight="600" sx={{ color: "#4a5568", fontSize: { xs: "0.9rem", sm: "1rem" } }}>
                          • {i.name}
                        </Typography>
                        <Chip label={`x${i.qty}`} size="small" sx={{ fontWeight: "bold", background: "#e2e8f0", color: "#475569" }} />
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: "#90a4ae", fontStyle: "italic" }}>
                      No items found.
                    </Typography>
                  )}
                </Stack>
              </CardContent>
            </Card>

            {/* REPORT BUTTON */}
            {orderStatus === "OUT_FOR_DELIVERY" && (
              <Box sx={{ mb: 2 }}>
                <Button
                  fullWidth
                  color="error"
                  variant="outlined"
                  startIcon={<ReportProblemOutlinedIcon />}
                  onClick={handleOpenCancel}
                  sx={{
                    borderRadius: "14px",
                    py: 1.2,
                    fontWeight: "700",
                    textTransform: "none",
                    borderColor: "red",
                    color: "red",
                    "&:hover": {
                      background: "rgba(224, 20, 20, 0.05)"
                    }
                  }}
                >
                  Report Order
                </Button>
              </Box>
            )}
          </Box>
        </Fade>

        {/* ========================= */}
        {/* 💳 PAYMENT DIALOG */}
        {/* ========================= */}
        <Dialog
          open={openPayment}
          onClose={actionLoading ? undefined : () => setOpenPayment(false)}
          fullScreen={isMobile}
          slotProps={{
            paper: {
              sx: {
                borderRadius: { xs: 0, sm: "24px" },
                padding: { xs: "1px 0px", sm: "16px" },
                background: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(20px)",
                boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
              }
            }
          }}
        >
          <DialogTitle sx={{ textAlign: "center", fontWeight: "800", color: "#1a237e", fontSize: { xs: "1.2rem", sm: "1.5rem" } }}>
            Complete Payment
          </DialogTitle>

          <DialogContent>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Typography
                  sx={{
                    px: 2.5,
                    borderRadius: "12px",
                    background: "rgba(30, 136, 229, 0.08)",
                    border: "1px solid rgba(30, 136, 229, 0.15)",
                    color: "#1565c0",
                    fontWeight: "700",
                    fontSize: { xs: "0.95rem", sm: "1rem" }
                  }}
                >
                  Order ID: #{id}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ textAlign: "center", mb: 3 }}>
              <Typography variant="body2" sx={{ color: "#78909c", textTransform: "uppercase", letterSpacing: "1px", fontWeight: "bold" }}>
                Amount to Collect
              </Typography>
              <Typography
                sx={{
                  fontWeight: "900",
                  color: "#2e7d32",
                  fontSize: { xs: "2.5rem", sm: "3rem" },
                  lineHeight: 1.2
                }}
              >
                ₹{upiData?.amount || data.final_amount}
              </Typography>
            </Box>

            {/* METHOD SELECTION */}
            <Box sx={{ display: "flex", flexDirection: "row", gap: 2, mb: 2 }}>
              <Button
                fullWidth
                variant={method === "COD" ? "contained" : "outlined"}
                onClick={() => setMethod("COD")}
                sx={{ borderRadius: "12px", py: 1.5, fontWeight: "bold", fontSize: "1.1rem" }}
              >
                💵 Cash
              </Button>
              <Button
                fullWidth
                variant={method === "UPI" ? "contained" : "outlined"}
                onClick={() => setMethod("UPI")}
                sx={{ borderRadius: "12px", py: 1.5, fontWeight: "bold", fontSize: "1.1rem" }}
              >
                📱 UPI
              </Button>
            </Box>

            {/* COD INPUT */}
            <Fade in={method === "COD"} mountOnEnter unmountOnExit>
              <Box sx={{ mt: 5 }}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label="Payment Note (e.g. Paid ₹250)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "12px", minHeight: "6rem"
                    }
                  }}
                />
              </Box>
            </Fade>

            {/* UPI QR */}
            <Fade in={method === "UPI"} mountOnEnter unmountOnExit>
              <Box>
                {upiData ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      background: "white",
                      p: { xs: 1, sm: 2 },
                      borderRadius: "16px",
                      border: "2px dashed #cfd8dc"
                    }}
                  >
                    <QRCodeCanvas
                      value={upiData.upi_url}
                      size={220}
                      level="H"
                      includeMargin
                    />
                    <Typography sx={{ mt: 0.5, fontWeight: "700", color: "#37474f", fontSize: { xs: "1rem", sm: "1.1rem" } }}>
                      Scan & Pay ₹{upiData.amount}
                    </Typography>
                    <Box sx={{ mt: 1 }}>
                      <Button
                        component="label"
                        variant="outlined"
                        fullWidth
                        sx={{ borderRadius: "12px", py: 1.5, fontWeight: "bold", mt: 2 }}
                      >
                        📸 Upload Payment Photo
                        <input
                          hidden
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            try {
                              // Compress the image down to ~500KB maximum
                              const options = {
                                maxSizeMB: 0.5, 
                                maxWidthOrHeight: 1024,
                                useWebWorker: true,
                              };
                              
                              const compressedFile = await imageCompression(file, options);
                              
                              // Pass the compressed, lightweight file to the state
                              setPaymentImage(compressedFile);
                            } catch (error) {
                              console.error("Compression error:", error);
                              alert("Failed to compress image. Please try again.");
                            }
                          }}
                        />
                      </Button>

                      {paymentImage && (
                        <Typography sx={{ mt: 1, fontSize: "0.9rem", color: "success.main" }}>
                          ✅ {paymentImage.name}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                    <CircularProgress />
                  </Box>
                )}
              </Box>
            </Fade>
          </DialogContent>

          <DialogActions 
            sx={{ 
              justifyContent: "space-between", // 👈 Pushes buttons to opposite ends
              px: { xs: 3, sm: 4 },            // 👈 Increased horizontal padding
              pb: { xs: 3, sm: 4 },            // 👈 Increased bottom padding
              pt: 2 
            }}
          >
            <Button
              onClick={() => setOpenPayment(false)}
              sx={{ 
                fontWeight: "600", 
                color: "#ef4444", // 👈 Red color matching the image
                fontSize: "0.95rem",
                letterSpacing: "0.5px"
              }}
            >
              CANCEL
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmitPayment}
              disabled={!method || (method === "UPI" && !paymentImage) || uploading || actionLoading}
              sx={{
                borderRadius: "24px", // 👈 Pill-shaped radius matching the image
                px: 4,
                py: 1,
                fontWeight: "700",
                fontSize: "0.95rem",
                letterSpacing: "0.5px",
                background: method ? "#1976d2" : "#e2e8f0", // 👈 Solid blue from image
                color: method ? "#ffffff" : "#94a3b8",
                boxShadow: method ? "0 4px 14px rgba(25, 118, 210, 0.3)" : "none",
                "&:hover": { background: method ? "#1565c0" : "#e2e8f0" },
                "&.Mui-disabled": { background: "#e2e8f0", color: "#94a3b8" } // Disabled state styles
              }}
            >
              {actionLoading ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : uploading ? (
                "UPLOADING..."
              ) : (
                "CONFIRM"
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ✅ DELIVERY SUCCESS DIALOG */}
        <Dialog
          open={successOpen}
          maxWidth="xs"
          fullWidth
          slotProps={{
            paper: {
              sx: {
                borderRadius: "28px",
                p: 2,
                textAlign: "center",
                background: "linear-gradient(135deg, #ffffff 0%, #f1f8e9 100%)",
                overflow: "hidden"
              }
            }
          }}
        >
          <DialogContent>
            <Box
              sx={{
                width: 90, height: 90, mx: "auto", borderRadius: "50%",
                background: "linear-gradient(135deg, #43a047 0%, #2e7d32 100%)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "3rem", color: "white", boxShadow: "0 10px 30px rgba(67, 160, 71, 0.35)", mb: 3, mt: 1
              }}
            >
              ✓
            </Box>
            <Typography sx={{ fontWeight: "800", fontSize: "1.5rem", color: "#1b5e20", mb: 1 }}>
              Delivery Successful
            </Typography>
            <Typography sx={{ color: "#546e7a", lineHeight: 1.7, fontSize: "0.95rem" }}>
              Payment collected successfully.
              <br />
              {data?.batch_id ? "Opening batch deliveries..." : "Redirecting to dashboard..."}
            </Typography>
          </DialogContent>
        </Dialog>

        <ReportOrderDialog
          open={openCancel}
          onClose={() => setOpenCancel(false)}
          cancelReason={cancelReason}
          setCancelReason={setCancelReason}
          showCancelInput={showCancelInput}
          setShowCancelInput={setShowCancelInput}
          confirmCancelOpen={confirmCancelOpen}
          setConfirmCancelOpen={setConfirmCancelOpen}
          handleCancelOrder={handleCancelOrder}
          actionLoading={actionLoading}
        />

        {/* 🔥 GLOBAL ACTION LOADER (Used for Payment & Cancellation Processing) */}
        <Backdrop
          open={actionLoading}
          sx={{
            color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 9999, backdropFilter: "blur(6px)",
            background: "rgba(0,0,0,0.45)", flexDirection: "column", gap: 2
          }}
        >
          <CircularProgress size={60} thickness={4} sx={{ color: "#ffffff" }} />
          <Typography sx={{ fontWeight: "700", fontSize: "1rem", letterSpacing: "0.5px" }}>
            {loadingText || "Please wait..."}
          </Typography>
        </Backdrop>
      </Box>
    </Box>
  );
}